import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import articles from './articles';
import { BlogArticle, getArticleLocale } from './types';
import { useLanguage } from '../i18n/LanguageContext';
import { useDynamicSEO } from '../hooks/useSEO';
import { AdBanner, AdSideRails, SLOT_HOMEPAGE_TOP, SLOT_HOMEPAGE_BOTTOM } from '../components/ads/Ads';
import './blog.css';

// Blog index (/blog): search bar + paginated article cards, newest first.
// Both the search query (?q=) and the page (?page=) live in the URL, so any
// filtered/paged view is shareable and crawlable. Each card shows the version
// matching the current UI language (fallback: English).
const PAGE_SIZE = 6;

// Search across ALL language versions of an article (title, excerpt, tags and
// the text content with HTML tags stripped), so e.g. typing "dictation" also
// finds articles whose Chinese version is shown.
const articleMatches = (article: BlogArticle, query: string): boolean => {
  if (!query) return true;
  const haystack = [
    ...article.tags,
    ...Object.values(article.locales).flatMap((locale) => [
      locale.title,
      locale.excerpt,
      locale.contentHtml.replace(/<[^>]*>/g, ' '),
    ]),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
};

const BlogList: React.FC = () => {
  const { lang, t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  useDynamicSEO(
    `${t('blog.title')} | StudyMind`,
    t('blog.subtitle'),
    'study tips, exam guides, DSE, dictation, flashcards, AI study tools'
  );

  const sorted = [...articles].sort((a, b) => (a.date < b.date ? 1 : -1));
  const dateLocale = lang === 'en' ? 'en-GB' : lang === 'zhHant' ? 'zh-HK' : 'zh-CN';

  // Search — the query lives in the URL (?q=); typing resets to page 1.
  const rawQuery = searchParams.get('q') || '';
  const query = rawQuery.trim().toLowerCase();
  const filtered = sorted.filter((article) => articleMatches(article, query));

  const handleSearchChange = (value: string) => {
    setSearchParams(value.trim() ? { q: value } : {}, { replace: true });
  };

  // Pagination — current page comes from the URL (?page=2), clamped to range.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const rawPage = parseInt(searchParams.get('page') || '1', 10);
  const page = Math.min(Math.max(Number.isNaN(rawPage) ? 1 : rawPage, 1), totalPages);
  const pageArticles = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (p: number) => {
    const params: Record<string, string> = {};
    if (rawQuery.trim()) params.q = rawQuery;
    if (p > 1) params.page = String(p);
    setSearchParams(params);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="blog-page">
      {/* Fixed skyscraper rails on wide screens (overlay, never shifts content) */}
      <AdSideRails />

      <header className="blog-index-header">
        <h1>{t('blog.title')}</h1>
        <p>{t('blog.subtitle')}</p>
      </header>

      <div className="blog-search">
        <span className="blog-search-icon" aria-hidden="true">
          🔍
        </span>
        <input
          type="search"
          className="blog-search-input"
          value={rawQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={t('blog.searchPlaceholder')}
          aria-label={t('blog.searchPlaceholder')}
        />
        {rawQuery && (
          <button
            type="button"
            className="blog-search-clear"
            onClick={() => handleSearchChange('')}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {query && (
        <p className="blog-results-count">
          {t('blog.results').replace('{count}', String(filtered.length))}
        </p>
      )}

      <AdBanner label="blog-top" slot={SLOT_HOMEPAGE_TOP} leaderboard style={{ margin: '0 auto 24px' }} />

      {pageArticles.length === 0 ? (
        <p className="blog-no-results">{t('blog.noResults')}</p>
      ) : (
        <div className="blog-card-list">
          {pageArticles.map((article) => {
            const locale = getArticleLocale(article, lang);
            if (!locale) return null;
            return (
              <Link key={article.slug} to={`/blog/${article.slug}`} className="blog-card">
                <h2>{locale.title}</h2>
                <p className="blog-card-excerpt">{locale.excerpt}</p>
                <div className="blog-meta">
                  <span>
                    {new Date(article.date).toLocaleDateString(dateLocale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                  <span>
                    {article.readingMinutes} {t('blog.minRead')}
                  </span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="blog-tag">
                      {tag}
                    </span>
                  ))}
                  <span className="blog-read-more">{t('blog.readMore')}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <nav className="blog-pagination" aria-label="Blog pages">
          <button
            type="button"
            className="blog-page-btn"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
          >
            {t('blog.prev')}
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              className={`blog-page-btn${p === page ? ' active' : ''}`}
              onClick={() => goToPage(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className="blog-page-btn"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages}
          >
            {t('blog.next')}
          </button>
        </nav>
      )}

      <AdBanner label="blog-bottom" slot={SLOT_HOMEPAGE_BOTTOM} leaderboard style={{ margin: '32px auto 0' }} />
    </div>
  );
};

export default BlogList;
